/**
 * Unified Doctor Onboarding Flow
 * Consolidates all doctor onboarding implementations into a single, properly connected system
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axiosInstance from '../../api/axiosInstance';
import unifiedDoctorOnboardingService from '../../services/api/unifiedDoctorOnboarding';
import {
    Box,
    Stepper,
    Step,
    StepLabel,
    StepContent,
    Button,
    Typography,
    Card,
    CardContent,
    Alert,
    CircularProgress,
    LinearProgress,
    Chip,
    Divider,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import {
    Person as PersonIcon,
    Work as WorkIcon,
    Description as DocumentIcon,
    Business as BusinessIcon,
    Schedule as ScheduleIcon,
    Payment as PaymentIcon,
    CheckCircle as CheckIcon,
    Warning as WarningIcon,
    Error as ErrorIcon,
    Refresh as RefreshIcon,
    NavigateNext as NextIcon,
    NavigateBefore as BackIcon,
    Home as HomeIcon,
    Save as SaveIcon,
    Help as HelpIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { useSessionManager } from '../../hooks/useSessionManager';
import { useDoctorOnboarding } from '../../hooks/useDoctorOnboarding';

const StyledCard = styled(Card)(({ theme }) => ({
    borderRadius: 16,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
    background: theme.palette.background.paper,
}));

const StyledButton = styled(Button)(({ theme }) => ({
    borderRadius: 12,
    textTransform: 'none',
    fontWeight: 600,
    padding: '12px 24px',
}));

const UnifiedDoctorOnboardingFlow = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    const { user } = useSelector((state) => state.auth);
    const { getSessionStats } = useSessionManager();

    // State management
    const [onboardingState, setOnboardingState] = useState(null);
    const [currentStep, setCurrentStep] = useState(0);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [completedSteps, setCompletedSteps] = useState(new Set());
    const [stepData, setStepData] = useState({});
    const [validationErrors, setValidationErrors] = useState({});
    const [showHelpDialog, setShowHelpDialog] = useState(false);

    // Check for intent parameter (from magic links)
    const intent = searchParams.get('intent');

    // Onboarding steps configuration
    const onboardingSteps = [{
            id: 'basic_info',
            title: 'Basic Information',
            description: 'Complete your basic profile and contact information',
            icon: < PersonIcon / > ,
            component: 'BasicInfoStep',
            required: true,
            estimatedTime: '3 minutes',
            fields: ['full_name', 'email', 'phone', 'specialization', 'license_number']
        },
        {
            id: 'professional_details',
            title: 'Professional Details',
            description: 'Add your professional qualifications and experience',
            icon: < WorkIcon / > ,
            component: 'ProfessionalDetailsStep',
            required: true,
            estimatedTime: '5 minutes',
            fields: ['qualifications', 'experience', 'certifications', 'education']
        },
        {
            id: 'documents',
            title: 'Document Verification',
            description: 'Upload and verify your professional documents',
            icon: < DocumentIcon / > ,
            component: 'DocumentVerificationStep',
            required: true,
            estimatedTime: '10 minutes',
            fields: ['license_document', 'id_document', 'qualification_documents']
        },
        {
            id: 'clinic_setup',
            title: 'Clinic Setup',
            description: 'Configure your clinic information and services',
            icon: < BusinessIcon / > ,
            component: 'ClinicSetupStep',
            required: true,
            estimatedTime: '8 minutes',
            fields: ['clinic_name', 'clinic_address', 'services', 'facilities']
        },
        {
            id: 'availability',
            title: 'Availability',
            description: 'Set your working hours and availability',
            icon: < ScheduleIcon / > ,
            component: 'AvailabilityStep',
            required: true,
            estimatedTime: '5 minutes',
            fields: ['working_hours', 'availability', 'timezone', 'consultation_duration']
        },
        {
            id: 'billing',
            title: 'Payment & Subscription',
            description: 'Set up your payment methods and subscription',
            icon: < PaymentIcon / > ,
            component: 'BillingStep',
            required: true,
            estimatedTime: '7 minutes',
            fields: ['payment_methods', 'subscription_plan', 'billing_info']
        }
    ];

    // Initialize onboarding
    useEffect(() => {
        initializeOnboarding();
    }, []);

    // Handle intent parameter
    useEffect(() => {
        if (intent) {
            handleIntent(intent);
        }
    }, [intent]);

    const initializeOnboarding = async() => {
        try {
            setLoading(true);
            setError(null);

            // Simulate API call to get onboarding state
            const state = await getOnboardingState();
            setOnboardingState(state);

            // Determine current step based on onboarding state
            const stepIndex = determineCurrentStep(state);
            setCurrentStep(stepIndex);

            // Set completed steps
            const completed = new Set(state.completed_sections || []);
            setCompletedSteps(completed);

            // Load existing data for each step
            await loadStepData();

        } catch (err) {
            console.error('Error initializing onboarding:', err);
            setError('Failed to load onboarding information. Please try again.');
            toast.error('Failed to load onboarding information');
        } finally {
            setLoading(false);
        }
    };

    const getOnboardingState = async() => {
        try {
            return await unifiedDoctorOnboardingService.getOnboardingState();
        } catch (error) {
            console.error('Error fetching onboarding state:', error);
            throw error;
        }
    };

    const determineCurrentStep = (state) => {
        const completedSections = state.completed_sections || [];

        for (let i = 0; i < onboardingSteps.length; i++) {
            const step = onboardingSteps[i];
            if (!completedSections.includes(step.id)) {
                return i;
            }
        }

        return onboardingSteps.length - 1; // All steps completed
    };

    const loadStepData = async() => {
        try {
            const data = {};

            // Load existing step data
            for (const step of onboardingSteps) {
                try {
                    const stepData = await getStepData(step.id);
                    data[step.id] = stepData;
                } catch (err) {
                    console.warn(`Failed to load data for step ${step.id}:`, err);
                }
            }

            setStepData(data);
        } catch (err) {
            console.error('Error loading step data:', err);
        }
    };

    const getStepData = async(stepId) => {
        // Simulate API call
        return {};
    };

    const handleIntent = (intent) => {
        const stepIndex = onboardingSteps.findIndex(step => step.id === intent);
        if (stepIndex !== -1) {
            setCurrentStep(stepIndex);
        }
    };

    const handleStepComplete = async(stepId, data) => {
        try {
            setSaving(true);
            setError(null);

            // Validate step data
            const validationResult = await validateStepData(stepId, data);
            if (!validationResult.isValid) {
                setValidationErrors(validationResult.errors);
                return;
            }

            // Save step data
            await saveStepData(stepId, data);

            // Mark step as complete
            await completeSection(stepId);

            // Update local state
            setCompletedSteps(prev => new Set([...prev, stepId]));
            setStepData(prev => ({...prev, [stepId]: data }));
            setValidationErrors({});

            // Move to next step
            const nextStepIndex = currentStep + 1;
            if (nextStepIndex < onboardingSteps.length) {
                setCurrentStep(nextStepIndex);
            } else {
                // All steps completed
                handleOnboardingComplete();
            }

            toast.success(`${onboardingSteps[currentStep].title} completed successfully!`);

        } catch (err) {
            console.error('Error completing step:', err);
            setError(`Failed to save ${onboardingSteps[currentStep].title}. Please try again.`);
            toast.error(`Failed to save ${onboardingSteps[currentStep].title}`);
        } finally {
            setSaving(false);
        }
    };

    const saveStepData = async(stepId, data) => {
        // Simulate API call
        console.log(`Saving step data for ${stepId}:`, data);
        return data;
    };

    const completeSection = async(stepId) => {
        // Simulate API call
        console.log(`Completing section ${stepId}`);
        return { success: true };
    };

    const validateStepData = async(stepId, data) => {
        const step = onboardingSteps.find(s => s.id === stepId);
        const errors = [];

        // Basic validation for required fields
        for (const field of step.fields) {
            if (!data[field] || data[field].toString().trim() === '') {
                errors.push(`${field} is required`);
            }
        }

        return {
            isValid: errors.length === 0,
            errors
        };
    };

    const handleOnboardingComplete = async() => {
        try {
            // Mark onboarding as complete
            await completeOnboarding();

            // Redirect to doctor dashboard
            navigate('/doctor-dashboard');

            toast.success('Welcome to MindWell! Your onboarding is complete.');

        } catch (err) {
            console.error('Error completing onboarding:', err);
            setError('Failed to complete onboarding. Please try again.');
            toast.error('Failed to complete onboarding');
        }
    };

    const completeOnboarding = async() => {
        // Simulate API call
        console.log('Completing onboarding');
        return { success: true };
    };

    const handleStepBack = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleStepNext = () => {
        if (currentStep < onboardingSteps.length - 1) {
            setCurrentStep(currentStep + 1);
        }
    };

    const handleSaveDraft = async() => {
        try {
            setSaving(true);

            // Save current step data as draft
            await saveStepData(onboardingSteps[currentStep].id, stepData[onboardingSteps[currentStep].id] || {});

            toast.success('Draft saved successfully!');

        } catch (err) {
            console.error('Error saving draft:', err);
            toast.error('Failed to save draft');
        } finally {
            setSaving(false);
        }
    };

    const handleRefresh = async() => {
        await initializeOnboarding();
    };

    const getStepStatus = (stepIndex) => {
        const step = onboardingSteps[stepIndex];

        if (completedSteps.has(step.id)) {
            return 'completed';
        } else if (stepIndex === currentStep) {
            return 'active';
        } else if (stepIndex < currentStep) {
            return 'pending';
        } else {
            return 'upcoming';
        }
    };

    const getStepIcon = (stepIndex) => {
        const status = getStepStatus(stepIndex);
        const step = onboardingSteps[stepIndex];

        switch (status) {
            case 'completed':
                return <CheckIcon color = "success" / > ;
            case 'active':
                return step.icon;
            case 'pending':
                return <WarningIcon color = "warning" / > ;
            default:
                return step.icon;
        }
    };

    const getProgressPercentage = () => {
        return (completedSteps.size / onboardingSteps.length) * 100;
    };

    const getEstimatedTimeRemaining = () => {
        const remainingSteps = onboardingSteps.slice(currentStep);
        return remainingSteps.reduce((total, step) => {
            const time = parseInt(step.estimatedTime);
            return total + (isNaN(time) ? 0 : time);
        }, 0);
    };

    if (loading) {
        return ( <
            Box display = "flex"
            flexDirection = "column"
            alignItems = "center"
            p = { 4 } >
            <
            CircularProgress size = { 48 }
            /> <
            Typography variant = "h6"
            sx = {
                { mt: 2 } } >
            Loading onboarding... <
            /Typography> <
            LinearProgress sx = {
                { width: '100%', mt: 2 } }
            /> <
            /Box>
        );
    }

    if (error) {
        return ( <
            Box p = { 3 } >
            <
            Alert severity = "error"
            sx = {
                { mb: 3 } } > { error } <
            /Alert> <
            Box display = "flex"
            gap = { 2 } >
            <
            StyledButton variant = "contained"
            onClick = { handleRefresh }
            startIcon = { < RefreshIcon / > } >
            Retry <
            /StyledButton> <
            StyledButton variant = "outlined"
            onClick = {
                () => navigate('/doctor-dashboard') }
            startIcon = { < HomeIcon / > } >
            Go to Dashboard <
            /StyledButton> <
            /Box> <
            /Box>
        );
    }

    return ( <
        Box p = { 3 } >
        <
        StyledCard >
        <
        CardContent sx = {
            { p: 4 } } > { /* Header */ } <
        Box mb = { 4 } >
        <
        Box display = "flex"
        justifyContent = "space-between"
        alignItems = "center"
        mb = { 2 } >
        <
        Typography variant = "h4"
        gutterBottom >
        Doctor Onboarding <
        /Typography> <
        Box display = "flex"
        gap = { 1 } >
        <
        Tooltip title = "Save Draft" >
        <
        IconButton onClick = { handleSaveDraft }
        disabled = { saving } >
        <
        SaveIcon / >
        <
        /IconButton> <
        /Tooltip> <
        Tooltip title = "Help" >
        <
        IconButton onClick = {
            () => setShowHelpDialog(true) } >
        <
        HelpIcon / >
        <
        /IconButton> <
        /Tooltip> <
        /Box> <
        /Box> <
        Typography variant = "body1"
        color = "text.secondary"
        paragraph >
        Complete your doctor profile to start practicing on MindWell.This will only take a few minutes. <
        /Typography>

        { /* Progress */ } <
        Box mb = { 3 } >
        <
        Box display = "flex"
        justifyContent = "space-between"
        mb = { 1 } >
        <
        Typography variant = "body2"
        color = "text.secondary" >
        Progress <
        /Typography> <
        Typography variant = "body2"
        color = "text.secondary" > { Math.round(getProgressPercentage()) } % Complete <
        /Typography> <
        /Box> <
        LinearProgress variant = "determinate"
        value = { getProgressPercentage() }
        color = "primary"
        sx = {
            { height: 8, borderRadius: 4 } }
        /> <
        /Box>

        { /* Time estimate */ } <
        Box display = "flex"
        gap = { 2 }
        mb = { 3 } >
        <
        Chip label = { `${getEstimatedTimeRemaining()} minutes remaining` }
        color = "primary"
        variant = "outlined"
        size = "small" /
        >
        <
        Chip label = { `Step ${currentStep + 1} of ${onboardingSteps.length}` }
        color = "secondary"
        variant = "outlined"
        size = "small" /
        >
        <
        /Box> <
        /Box>

        { /* Stepper */ } <
        Stepper activeStep = { currentStep }
        orientation = "vertical" > {
            onboardingSteps.map((step, index) => ( <
                Step key = { step.id }
                completed = { completedSteps.has(step.id) } >
                <
                StepLabel icon = { getStepIcon(index) }
                optional = { <
                    Box >
                    <
                    Typography variant = "caption"
                    color = "text.secondary" > { step.estimatedTime } <
                    /Typography> {
                        !step.required && ( <
                            Chip label = "Optional"
                            size = "small"
                            color = "info"
                            variant = "outlined"
                            sx = {
                                { ml: 1 } }
                            />
                        )
                    } <
                    /Box>
                } >
                <
                Typography variant = "h6" > { step.title } <
                /Typography> <
                Typography variant = "body2"
                color = "text.secondary" > { step.description } <
                /Typography> <
                /StepLabel> <
                StepContent >
                <
                Box > { /* Step content will be rendered here */ } <
                Typography variant = "body1" >
                Step content
                for { step.title }
                will be rendered here. <
                /Typography>

                { /* Navigation buttons */ } <
                Box display = "flex"
                gap = { 2 }
                mt = { 3 } > {
                    index > 0 && ( <
                        StyledButton variant = "outlined"
                        onClick = { handleStepBack }
                        startIcon = { < BackIcon / > } >
                        Back <
                        /StyledButton>
                    )
                }

                <
                StyledButton variant = "contained"
                onClick = {
                    () => handleStepComplete(step.id, stepData[step.id] || {}) }
                disabled = { saving }
                startIcon = { saving ? < CircularProgress size = { 20 } /> : <NextIcon / > } >
                { saving ? 'Saving...' : 'Complete & Continue' } <
                /StyledButton>

                <
                StyledButton variant = "outlined"
                onClick = { handleSaveDraft }
                disabled = { saving }
                startIcon = { < SaveIcon / > } >
                Save Draft <
                /StyledButton> <
                /Box> <
                /Box> <
                /StepContent> <
                /Step>
            ))
        } <
        /Stepper>

        { /* Validation errors */ } {
            Object.keys(validationErrors).length > 0 && ( <
                Alert severity = "error"
                sx = {
                    { mt: 3 } } >
                <
                Typography variant = "body2" >
                Please fix the following errors:
                <
                /Typography> <
                ul > {
                    Object.entries(validationErrors).map(([field, error]) => ( <
                        li key = { field } > { error } < /li>
                    ))
                } <
                /ul> <
                /Alert>
            )
        }

        { /* Session status */ } <
        Box mt = { 4 } >
        <
        Divider / >
        <
        Box display = "flex"
        justifyContent = "space-between"
        alignItems = "center"
        mt = { 2 } >
        <
        Typography variant = "body2"
        color = "text.secondary" >
        Session Status: { getSessionStats().status } <
        /Typography> <
        Tooltip title = "Refresh onboarding data" >
        <
        IconButton onClick = { handleRefresh }
        size = "small" >
        <
        RefreshIcon / >
        <
        /IconButton> <
        /Tooltip> <
        /Box> <
        /Box> <
        /CardContent> <
        /StyledCard>

        { /* Help Dialog */ } <
        Dialog open = { showHelpDialog }
        onClose = {
            () => setShowHelpDialog(false) }
        maxWidth = "md" >
        <
        DialogTitle > Doctor Onboarding Help < /DialogTitle> <
        DialogContent >
        <
        Typography variant = "body1"
        paragraph >
        This onboarding process will help you set up your doctor profile on MindWell.You can save your progress at any time and
        return later to complete it. <
            /Typography> <
            Typography variant = "h6"
        gutterBottom >
        Steps Overview:
        <
        /Typography> <
        ul > {
            onboardingSteps.map((step, index) => ( <
                li key = { step.id } >
                <
                strong > { step.title } < /strong> - {step.description} ({step.estimatedTime}) <
                /li>
            ))
        } <
        /ul> <
        /DialogContent> <
        DialogActions >
        <
        Button onClick = {
            () => setShowHelpDialog(false) } > Close < /Button> <
        /DialogActions> <
        /Dialog> <
        /Box>
    );
};

export default UnifiedDoctorOnboardingFlow;